"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

type HeaderNavChild = {
  href: string;
  label: string;
};

type HeaderNavItem = {
  href: string;
  label: string;
  children?: HeaderNavChild[];
};

function getActiveHref(pathname: string, items: HeaderNavItem[]): string | null {
  let activeHref: string | null = null;
  let maxMatchLength = 0;

  for (const item of items) {
    const isMatch = pathname === item.href || pathname.startsWith(`${item.href}/`);
    if (isMatch && item.href.length > maxMatchLength) {
      activeHref = item.href;
      maxMatchLength = item.href.length;
    }
  }

  return activeHref;
}

export function InventoryHomeHeaderNav({
  items,
  ariaLabel,
}: {
  items: HeaderNavItem[];
  ariaLabel: string;
}) {
  const pathname = usePathname() ?? "";
  const activeHref = getActiveHref(pathname, items);

  return (
    <nav aria-label={ariaLabel} className="hidden h-20 items-center space-x-6 2xl:space-x-8 xl:flex">
      {items.map((item) => {
        const active = item.href === activeHref;
        const hasChildren = Boolean(item.children && item.children.length > 0);
        const hoverClass = hasChildren ? "group-hover:text-brand-red" : "hover:text-brand-red";
        const linkClassName = `inline-flex items-center gap-1 border-b-2 pb-1 text-fs-xl font-semibold uppercase tracking-wide transition-colors whitespace-nowrap ${
          active ? "border-brand-red text-brand-red" : `border-transparent text-charcoal-700 ${hoverClass}`
        }`;

        if (hasChildren) {
          return (
            <div key={item.href} className="group relative flex h-full items-center">
              <Link href={item.href} className={linkClassName}>
                {item.label}
                <svg
                  className="h-3 w-3 text-charcoal-400 transition-transform duration-200 group-hover:-rotate-180 group-hover:text-brand-red"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Link>

              <div className="invisible absolute left-0 top-full z-50 w-64 translate-y-2 border-t-2 border-brand-red bg-white opacity-0 shadow-xl transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div className="flex flex-col py-2">
                  {item.children!.map((child) => (
                    <Link
                      key={child.href}
                      href={child.href}
                      className="px-5 py-2.5 text-fs-lg font-medium text-charcoal-700 transition-colors hover:bg-gray-50 hover:text-brand-red"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        }

        return (
          <Link key={item.href} href={item.href} className={linkClassName}>
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}
